import React from 'react';
import { DateTime } from 'luxon';
import Stack from '@mui/material/Stack';
import DetailsCard from '@common/components/General/DetailsCard';
import { useTranslation } from 'react-i18next';
import TextIcon from '../misc/TextIcon';
import StatusIcon from './StatusIcon';
import Mission from '../../models/missions/mission';
import CustomLocalizationType from '../../models/enums/custom-localization-type';
import {
  BatteryEndIcon,
  BatteryStartIcon,
  CleanedAreaIcon,
  LocationsIcon,
  OrganizationsIcon,
  PerformanceIcon,
  RoomIcon,
  TimezoneIcon,
} from '@common/utils/icons/icons';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import OrganizationRole from '../../models/enums/organization-roles';
import { dateToStringFormat } from '@common/utils/date';
import useCustomLocalizationEffect from '../../hooks/useCustomLocalizationEffect';
import { TimeIcon } from '@mui/x-date-pickers';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import MissionEndStatus from '../../models/enums/mission-status';
import DistanceUnitSet from '@common/components/misc/DistanceUnitSet';
import { DistanceValueUnit } from '@common/enums/configuration/units';
import { Theme } from '@mui/material';
import useMediaQuery from "@mui/material/useMediaQuery";

const MissionDetails: React.FC<{ mission: Mission }> = ({ mission }) => {
  const { t } = useTranslation();
  const localize = useCustomLocalizationEffect();
  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const roomNames = Array.from(
    new Set(mission.runs.filter((run) => run.roomName != null).map((run) => run.roomName)),
  );
  const orderedMissionRuns = [...mission.runs].sort(
    (a, b) =>
      DateTime.fromISO(a.cleaningStart).toMillis() - DateTime.fromISO(b.cleaningStart).toMillis(),
  );
  const firstRunWithErrors = [...orderedMissionRuns].reverse().find((run) => {
    return run.errors.length > 0;
  });

  return (
    <React.Fragment>
      <Stack spacing={2}>
        <DetailsCard cardTitle={t('label-state')}>
          <Stack spacing={2} pb={2}>
            {firstRunWithErrors?.errors[0] ? (
              <CustomAlert
                sx={{ '.MuiAlert-message': { width: '100%' } }}
                header={
                  <Typography>
                    {`${t('label-mission-end-status-' + mission.missionEndStatus)} -
                        ${firstRunWithErrors?.errors[0].errorType != null
                        ? localize(
                          firstRunWithErrors?.errors[0].errorType,
                          CustomLocalizationType.ERROR_CODE,
                        )
                        : localize(
                          firstRunWithErrors?.errors[0].maintenanceType,
                          CustomLocalizationType.MAINTENANCE_CODE,
                        )
                      }`}
                  </Typography>
                }
                severity={
                  mission.missionEndStatus === MissionEndStatus.SUCCEEDED_WITH_ERROR
                    ? 'info'
                    : firstRunWithErrors?.errors[0].errorType != null
                      ? 'error'
                      : 'warning'
                }
                textMessages={(() => {
                  const message =
                    firstRunWithErrors?.errors[0].errorType != null
                      ? localize(
                        firstRunWithErrors?.errors[0].errorType,
                        CustomLocalizationType.ERROR_CODE,
                        true,
                      )
                      : localize(
                        firstRunWithErrors?.errors[0].maintenanceType,
                        CustomLocalizationType.MAINTENANCE_CODE,
                        true,
                      );
                  return message != null ? [message] : undefined;
                })()}
              />
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                pr={1}
                flexWrap="wrap"
              >
                <TextIcon
                  text={t('label-mission-end-status-' + mission.missionEndStatus)}
                  icon={<StatusIcon status={mission.missionEndStatus} />}
                  startIcon
                />
              </Stack>
            )}
            <TextIcon
              text={mission.deployment.organizationName}
              icon={
                mission.organization.role === OrganizationRole.LOCATION ? (
                  <LocationsIcon color="action" />
                ) : (
                  <OrganizationsIcon color="action" />
                )
              }
              startIcon
            />
            <TextIcon
              text={roomNames.length > 0 ? roomNames[0]! : t('unknown-room-name')}
              icon={<RoomIcon color="action" />}
              startIcon
            />
            {mission.runs.length > 0 && (
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextIcon
                  text={`${mission.runs[0].batteryPercentageStart}% ${t('table-header-run-battery-start')}`}
                  icon={<BatteryStartIcon color="action" />}
                  startIcon
                />
                <TextIcon
                  text={`${mission.runs[mission.runs.length - 1].batteryPercentageEnd}% ${t(
                    'table-header-run-battery-end',
                  )}`}
                  icon={<BatteryEndIcon color="action" />}
                  startIcon
                />
              </Stack>
            )}
          </Stack>
        </DetailsCard>
        <DetailsCard cardTitle={t('scheduling-situation')}>
          <Stack spacing={2} pb={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TimeIcon color="action" />
              {matchesSmDown ? (
                <Stack>
                  <Typography variant="body2">
                    {dateToStringFormat(mission.startTime, false, mission.timezone)}
                  </Typography>
                  <Typography variant="body2">
                    {dateToStringFormat(mission.endTime, false, mission.timezone)}
                  </Typography>
                </Stack>
              ) : (
                <Typography>
                  {`${dateToStringFormat(mission.startTime, false, mission.timezone)} - ${dateToStringFormat(
                    mission.endTime,
                    false,
                    mission.timezone,
                  )}`}
                </Typography>
              )}
            </Stack>
            <TextIcon text={mission.timezone} icon={<TimezoneIcon color="action" />} startIcon />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Tooltip title={t('info-tooltip-cleaning-performance')}>
                <span>
                  <TextIcon
                    text={
                      <DistanceUnitSet
                        value={
                          mission.duration > 0 ? mission.cleanedArea / (mission.duration / 3600) : 0
                        }
                        valueUnit={DistanceValueUnit.METERS_SQUARED_PER_HOUR}
                        round
                      />
                    }
                    icon={<PerformanceIcon color="action" />}
                    startIcon
                  />
                </span>
              </Tooltip>
              <Tooltip title={t('info-tooltip-cleaned-area')}>
                <span>
                  <TextIcon
                    text={
                      <DistanceUnitSet
                        value={mission.cleanedArea}
                        round
                        valueUnit={DistanceValueUnit.METERS_SQUARED}
                      />
                    }
                    icon={<CleanedAreaIcon color="action" />}
                    startIcon
                  />
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </DetailsCard>
      </Stack>
    </React.Fragment>
  );
};

export default MissionDetails;
